import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { HttpRequestMethods, IDP_APPS } from '../../../../../constants/utils';
import { EndpointKeysCommon } from '../../../../../constants/utils/app.constants';
import {
  DashboardResponse,
  LoginAndGetDashboardRequest,
  LoginAndGetDashboardResponse,
  LogoutResponse,
  OauthAEMConfig
} from '../../../../../models/login';
import { HttpHeadersPayload } from '../../../../../models/utils';
import { ApiBuilder, AppConfigService, handleError } from '../../../../utils';

@Injectable({
  providedIn: 'root'
})
export class UserDataService {
  errorHandler = handleError.bind(this);

  authConfigJson!: Observable<OauthAEMConfig>;

  constructor(private http: HttpClient, private appConfig: AppConfigService) {}

  getAuthConfigContent(): Observable<OauthAEMConfig> {
    if (!this.authConfigJson) {
      const authConfig = this.appConfig.getConfig('AUTH_CONFIG');
      this.authConfigJson = authConfig
        ? of(authConfig as unknown as OauthAEMConfig).pipe(shareReplay(1))
        : this.getAuthConfig().pipe(shareReplay(1));
    }
    return this.authConfigJson;
  }

  getAuthConfig = (): Observable<OauthAEMConfig> => {
    return new ApiBuilder<undefined, OauthAEMConfig>(this.http, this.appConfig)
      .app(IDP_APPS.COMMON)
      .endpointKey(EndpointKeysCommon.APP_AUTH_CONFIG_JSON)
      .method(HttpRequestMethods['GET'])
      .timeOut(10000)
      .errorHandler(this.errorHandler)
      .library('SHARED')
      .create();
  };

  private getDashBrdDataPayload(
    config: OauthAEMConfig,
    tokenResponse: string
  ): Record<string, string | number> {
    if (config.TOKEN_HEADER && config.ENABLE_NEW_PERSISTENT_LOGIN) {
      return {};
    } else if (config.ENABLE_NEW_PERSISTENT_LOGIN) {
      return { eamToken: tokenResponse };
    } else {
      return {};
    }
  }

  getDashBrdData = (
    config: OauthAEMConfig,
    tokenResponse: string
  ): Observable<DashboardResponse> => {
    const headers: HttpHeadersPayload = {
      'Content-Type': 'application/json'
    };

    if (tokenResponse && config.TOKEN_HEADER) {
      headers['Authorization'] = 'Bearer ' + tokenResponse;
    }

    return new ApiBuilder<Record<string, string | number>, DashboardResponse>(
      this.http,
      this.appConfig
    )
      .app(IDP_APPS.COMMON)
      .endpointKey(EndpointKeysCommon['DASHBOARD_BFF_URL'])
      .method(HttpRequestMethods['POST'])
      .timeOut(config?.LOGIN_TIMEOUT ? config.LOGIN_TIMEOUT : 10000)
      .payload(this.getDashBrdDataPayload(config, tokenResponse))
      .httpHeaders(headers)
      .errorHandler(this.errorHandler)
      .library('SHARED')
      .create();
  };

  loginAndGetDashBrdData = (
    payload: LoginAndGetDashboardRequest,
    tokenResponse?: string,
    config?: OauthAEMConfig
  ): Observable<LoginAndGetDashboardResponse> => {
    const headers: HttpHeadersPayload = {
      'Content-Type': 'application/json'
    };

    if (tokenResponse) {
      headers['Authorization'] = 'Bearer ' + tokenResponse;
    }

    return new ApiBuilder<
      LoginAndGetDashboardRequest,
      LoginAndGetDashboardResponse
    >(this.http, this.appConfig)
      .app(IDP_APPS.LOGIN_MODAL)
      .endpointKey(EndpointKeysCommon['LOGIN_DASHBOARD_BFF_URL'])
      .method(HttpRequestMethods['POST'])
      .timeOut(config?.LOGIN_TIMEOUT ? config.LOGIN_TIMEOUT : 30000)
      .payload(payload)
      .httpHeaders(headers)
      .errorHandler(this.errorHandler)
      .library('SHARED')
      .create();
  };

  bffLogout = (config: OauthAEMConfig): Observable<Partial<LogoutResponse>> => {
    const headers: HttpHeadersPayload = {
      'Content-Type': 'application/json'
    };

    return new ApiBuilder<
      Record<string, string | number>,
      Partial<LogoutResponse>
    >(this.http, this.appConfig)
      .app(IDP_APPS.COMMON)
      .endpointKey(EndpointKeysCommon['LOGOUT_URL'])
      .method(HttpRequestMethods['POST'])
      .timeOut(config?.LOGIN_TIMEOUT ? config.LOGIN_TIMEOUT : 10000)
      .payload({})
      .httpHeaders(headers)
      .errorHandler(this.errorHandler)
      .library('SHARED')
      .create();
  };
}
